import { useCallback, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSetRecoilState } from 'recoil';
import { useActiveEditTarget } from '~/hooks/target/useActiveEditTarget';
import { VoxelSubRange } from '~/domain/services/imageService/gridGeometry';
import { SDFOperations } from '~/engine/util/algorithms/sdf/SDFOperations';
import { useContourEditUndoOffer } from '~/hooks/undo/useContourEditUndoOffer';
import { contourWriteInFlightState } from '~/state/atoms/contourDeletionAtoms';
import { enqueueSnackbar } from '~/utils/notifications';
import { NOTIFICATION_AUTO_HIDE_MS } from '~/utils/notifications';
import { formatMm3AsCm3 } from '~/utils/units';
import { useRoiSdfOperation } from './useRoiSdfOperation';

// ── Deleting whole curves inside a world-space box ───────────────────
//
// One implementation behind every gesture that picks curves off the
// image: the dragged rectangle, and the single curve the viewport menu
// picks under the cursor. They differ by their box and their wording and
// by nothing else, so neither can acquire a safety property the other
// lacks.
//
// The write commits with no result to check first, so the report carries
// the way back (see useContourEditUndoOffer).

/** What a clear did, for a caller that reports the outcomes differently. */
export type ClearCurvesOutcome =
    /** Curves went; the report and its Undo are on screen. */
    | 'cleared'
    /** The box held no contour. No write, no undo entry, no report. */
    | 'nothing'
    /** Refused or failed; the reason is already on screen. */
    | 'failed';

export interface ClearCurvesMessages {
    /** The report. Formatted with `{name}`, `{curves}`, `{removed}`. */
    successMessageId: string;
    failedMessageId: string;
    /** Shown when the box would take the whole structure. */
    wouldEmptyMessageId: string;
    undoLabelId: string;
    undoUnavailableMessageId: string;
    /** Names the write in the ROI's contour history and the audit trail. */
    operationName: string;
    logLabel: string;
}

/**
 * Clears every in-plane connected part the box touches, taking each part
 * whole — including the stretch of it lying outside the box.
 *
 * @param structureSetUid keys the ROI's contour history for the Undo the
 *   report carries. Pinned at the call, not read from the live selection.
 */
export function useClearCurvesInBox(
    structureSetUid: string | undefined,
    messages: ClearCurvesMessages
) {
    const intl = useIntl();
    // The edit target, so the clear lands on the Environment the user is
    // editing.
    const { environment, roiNumber: selectedROI } = useActiveEditTarget();
    const { executeOperation } = useRoiSdfOperation();
    // Held in a ref and kept out of the callback's deps: the wording is
    // fixed per call site, and a caller passing the object inline would
    // otherwise rebuild the handler on every render — which the
    // rectangle's consuming effect depends on for its identity.
    const messagesRef = useRef(messages);
    messagesRef.current = messages;
    // The labelling pass runs BEFORE executeOperation is entered, so
    // `sdfOperationInFlightState` does not cover it; without this a second
    // gesture could start a second pass against the same ROI while the
    // first was still counting. Published globally as well as locally so
    // every surface that must not start a second write reads one fact.
    const [isClearing, setIsClearingLocal] = useState(false);
    const setWriteInFlight = useSetRecoilState(contourWriteInFlightState);
    const setIsClearing = useCallback(
        (value: boolean) => {
            setIsClearingLocal(value);
            setWriteInFlight(value);
        },
        [setWriteInFlight]
    );

    const undoOffer = useContourEditUndoOffer(structureSetUid, selectedROI, {
        undoLabelId: messages.undoLabelId,
        unavailableMessageId: messages.undoUnavailableMessageId,
    });

    const clearCurvesInBox = useCallback(
        async (range: VoxelSubRange): Promise<ClearCurvesOutcome> => {
            const roi = environment?.structureSet?.getRoi(selectedROI);
            if (!roi?.texture) {
                // Both arm paths refuse without a real ROI and grid, so
                // this is only reachable if the selection changed between
                // the gesture starting and its box landing. Reported
                // rather than left to do nothing silently.
                enqueueSnackbar(
                    intl.formatMessage({
                        id: messagesRef.current.failedMessageId,
                    }),
                    { variant: 'error' }
                );
                return 'failed';
            }

            setIsClearing(true);

            let result: Awaited<
                ReturnType<typeof SDFOperations.applyClearPartsInWindow>
            >;
            try {
                // Both gestures arrive already resolved to voxels — the
                // drag through gridSubRange on its own in-plane extent, the
                // pick as the single voxel it found. Nothing is converted
                // back out of a world box here, which is what would round
                // the window outward.
                result = await SDFOperations.applyClearPartsInWindow(
                    roi.texture,
                    range
                );
            } catch (error) {
                console.error(`${messagesRef.current.logLabel} error:`, error);
                enqueueSnackbar(
                    intl.formatMessage({
                        id: messagesRef.current.failedMessageId,
                    }),
                    { variant: 'error' }
                );
                return 'failed';
            } finally {
                setIsClearing(false);
            }

            if (!result) {
                // GlOperationGuard was held by another SDF op in flight —
                // not an exception, but still a failure the user needs to
                // be told about rather than a gesture that just does
                // nothing.
                enqueueSnackbar(
                    intl.formatMessage({
                        id: messagesRef.current.failedMessageId,
                    }),
                    { variant: 'error' }
                );
                return 'failed';
            }

            if (result.clearedVoxelCount === 0) {
                // The wrapper's no-op returns the SOURCE texture. It must
                // not be deleted (that would destroy the live structure's
                // own texture) and must not reach executeOperation, which
                // would replaceTexture an identical texture and cost an
                // undo step.
                if (result.texture !== roi.texture) {
                    result.texture.delete();
                }
                return 'nothing';
            }

            // Read before the closure so the conversion uses the grid the
            // clear was counted on, whatever the selection does next.
            const grid = roi.texture.textureGrid;
            const voxelVolumeMm3 = grid.resX * grid.resY * grid.resZ;
            // `result` is a `let`; its narrowing does not survive into the
            // closure below.
            const cleared = result;
            // The cleared texture is a GPU allocation this call site OWNS
            // until `operationFn` hands it to the ROI. executeOperation can
            // return WITHOUT ever calling operationFn — its busy guard, or
            // a validation failure — and on those paths nothing else would
            // release it. Ownership is tracked explicitly and the texture
            // is deleted on every path that did not transfer it. Kept in a
            // local, never in state: a post-unmount state write is dropped
            // and the allocation would leak.
            let handedOff = false;

            // One live Undo at a time — the previous offer can no longer
            // act once this write lands, so it is retired as this one is
            // dispatched rather than left on screen to decline.
            undoOffer.closePreviousOffer();
            // Before the write: the step this clear records does not exist
            // until the ROI's change has been published and handled, so the
            // baseline has to be the edit it is about to supersede.
            undoOffer.captureStep();

            try {
                await executeOperation({
                    operationFn: async () => {
                        handedOff = true;
                        // Always 'changed': the zero-cleared case returned
                        // above without ever reaching the tracked write, so
                        // by here curves have gone.
                        return {
                            status: 'changed' as const,
                            texture: cleared.texture,
                            successValues: {
                                // Both halves of what went, because in 3D
                                // the gesture reaches curves on slices the
                                // user is not looking at: the count says
                                // how many curves it took, the volume says
                                // how much of the structure that was.
                                curves: cleared.clearedPartCount,
                                // Voxels are an engine unit and never reach
                                // the user, so the count is converted here,
                                // at the UI boundary, on the spacings of
                                // the very grid the clear counted on.
                                removed: formatMm3AsCm3(
                                    cleared.clearedVoxelCount * voxelVolumeMm3
                                ),
                            },
                        };
                    },
                    operationName: messagesRef.current.operationName,
                    successMessageId: messagesRef.current.successMessageId,
                    failedMessageId: messagesRef.current.failedMessageId,
                    // A box covering the whole structure is refused by the
                    // shared empty-result gate. Named here so the refusal
                    // points at the deliberate way to empty a structure
                    // instead of reading as an unexplained failure.
                    emptyResultMessageId:
                        messagesRef.current.wouldEmptyMessageId,
                    logLabel: messagesRef.current.logLabel,
                    // The clear commits with no preview to check first
                    // and, in 3D, takes curves on slices the clinician
                    // never looked at, so the toast that reports it
                    // carries the way back — at warning length, because a
                    // 5 s success toast is not an affordance anyone can
                    // act on.
                    successSnackbarOptions: {
                        autoHideDuration: NOTIFICATION_AUTO_HIDE_MS,
                        action: undoOffer.action,
                    },
                });
            } finally {
                if (!handedOff) {
                    cleared.texture.delete();
                }
            }

            return handedOff ? 'cleared' : 'failed';
        },
        [
            environment,
            selectedROI,
            executeOperation,
            intl,
            undoOffer,
            setIsClearing,
        ]
    );

    return { clearCurvesInBox, isClearing };
}
